import { T } from 'consts/translationKeys'
import { useState, useLayoutEffect, useCallback, useRef } from 'react'
import { translate } from 'utils/translations'

import { useConfirmationPopup } from 'hooks/useConfirmationPopup'
import { SOUNDS_NO_RESULT_PNG } from 'assets/images'
import { dispatch } from 'store'
import { audioThunks } from 'store/features/audio/thunks'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { ProjectIds } from './already-used-ids'
import { ListOnScrollProps } from 'react-window'

export const useCheckCanScroll = (
  soundsListRef: HTMLDivElement | null,
  totalContentHeight: number,
  visibleHeight: number
) => {
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  useLayoutEffect(() => {
    if (soundsListRef) {
      // Initial check if content is scrollable
      setCanScrollDown(totalContentHeight > visibleHeight)
    }
  }, [soundsListRef, totalContentHeight, visibleHeight])

  const onScroll = (event: ListOnScrollProps) => {
    const { scrollOffset } = event

    // Check if we can scroll up (not at the top)
    setCanScrollUp(scrollOffset > 0)

    // Check if we can scroll down (not at the bottom)
    setCanScrollDown(scrollOffset + visibleHeight < totalContentHeight)
  }

  return { canScrollUp, canScrollDown, onScroll }
}

export const useListHeight = (
  containerRef: HTMLDivElement | null,
  dependencies: unknown[] = []
) => {
  const [listHeight, setListHeight] = useState<number>(400) // Default fallback height
  const headerElementsRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const calculateHeight = () => {
      if (containerRef && headerElementsRef.current) {
        const containerHeight = containerRef.clientHeight
        const headerHeight = headerElementsRef.current.clientHeight
        const availableHeight = containerHeight - headerHeight

        if (availableHeight > 0) {
          setListHeight(availableHeight)
        }
      }
    }

    calculateHeight()

    // Add resize observer to handle dynamic height changes
    const resizeObserver = new ResizeObserver(calculateHeight)

    if (containerRef) {
      resizeObserver.observe(containerRef)
    }

    if (headerElementsRef.current) {
      resizeObserver.observe(headerElementsRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [containerRef, dependencies])

  return { listHeight, headerElementsRef }
}

export const useSoundsListCallbacks = () => {
  const [lastDeletedSoundIdRef, setLastDeletedSoundIdRef] = useState<
    number | null
  >(null)

  const showConfirmation = useConfirmationPopup()
  const { setAlertPopupsData } = useAlertPopupsContext()

  const onShowUsageWarning = useCallback(
    (templateProjectIds: number[], videoProjectIds: number[]) => {
      setAlertPopupsData({
        warningPopup: {
          title: translate(T.DELETE_SOUND_FROM_LIBRARY_WARNING_MESSAGE),
          imageSrc: SOUNDS_NO_RESULT_PNG,
          description: (
            <ProjectIds
              templateProjectIds={templateProjectIds}
              videoProjectIds={videoProjectIds}
            />
          ),
          buttons: {
            confirm: { label: translate(T.CLOSE_LABEL) }
          }
        }
      })
    },
    [setAlertPopupsData]
  )

  const deleteSound = useCallback(
    (soundId: number | null) => {
      if (soundId) {
        const onDeleteComplete = () => {
          setLastDeletedSoundIdRef(null)
        }

        dispatch(
          audioThunks.deleteUsersSound(
            soundId,
            onDeleteComplete,
            onShowUsageWarning
          )
        )

        setLastDeletedSoundIdRef(soundId)
      }
    },
    [onShowUsageWarning]
  )

  const setDeletableSoundIdCallback = useCallback(
    (id: number) => {
      showConfirmation({
        title: translate(T.REMOVE_SOUND_FROM_LIBRARY),
        imageSrc: SOUNDS_NO_RESULT_PNG,
        confirmButtonText: translate(T.DELETE_LABEL),
        cancelButtonText: translate(T.CANCEL_LABEL),
        onConfirm: () => {
          deleteSound(id)
        }
      })
    },
    [showConfirmation, deleteSound]
  )

  return {
    setDeletableSoundIdCallback,
    lastDeletedSoundIdRef
  }
}
